import { useState } from 'react'

export type TableSelection<Filter> =
  { mode: 'ids'; ids: string[] } | { mode: 'all'; filter: Filter; totalCount: number }

export const emptyTableSelection = <Filter>(): TableSelection<Filter> => ({ mode: 'ids', ids: [] })

type FilterScopedSelection<Filter> = { filterKey: string; selection: TableSelection<Filter> }

export function selectionForFilterKey<Filter>(
  state: FilterScopedSelection<Filter>,
  filterKey: string,
): TableSelection<Filter> {
  return state.filterKey === filterKey ? state.selection : emptyTableSelection()
}

export function downgradeAllSelection<Filter>(
  selection: TableSelection<Filter>,
  pageIds: readonly string[],
  toggledId: string,
): TableSelection<Filter> {
  if (selection.mode === 'all') {
    return { mode: 'ids', ids: pageIds.filter((id) => id !== toggledId) }
  }
  return {
    mode: 'ids',
    ids: selection.ids.includes(toggledId)
      ? selection.ids.filter((id) => id !== toggledId)
      : [...selection.ids, toggledId],
  }
}

export function deselectTableRow<Filter>(
  selection: TableSelection<Filter>,
  pageIds: readonly string[],
  removedId: string,
): TableSelection<Filter> {
  return {
    mode: 'ids',
    ids: (selection.mode === 'all' ? pageIds : selection.ids).filter((id) => id !== removedId),
  }
}

export function useTableSelection<Filter>({
  pageIds,
  filter,
  filterKey,
  totalCount,
}: {
  pageIds: readonly string[]
  filter: Filter
  // filter オブジェクトの参照は描画ごとに変わり得るため、呼び出し側で安定して直列化したキーを渡す。
  filterKey: string
  totalCount: number
}) {
  const [state, setState] = useState<FilterScopedSelection<Filter>>({
    filterKey,
    selection: emptyTableSelection(),
  })
  if (state.filterKey !== filterKey) {
    setState({ filterKey, selection: emptyTableSelection() })
  }
  // filter が変わったレンダーでも古い選択を露出させない。
  const selection = selectionForFilterKey(state, filterKey)

  const setSelection = (update: (current: TableSelection<Filter>) => TableSelection<Filter>) => {
    setState((current) => ({ filterKey, selection: update(selectionForFilterKey(current, filterKey)) }))
  }

  const selectedOnPage = selection.mode === 'all' ? pageIds : pageIds.filter((id) => selection.ids.includes(id))
  const allPageSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length
  const somePageSelected = selectedOnPage.length > 0 && !allPageSelected
  const selectedCount = selection.mode === 'all' ? selection.totalCount : selection.ids.length

  const toggleSelectAllPage = () => {
    setSelection((current) => {
      if (current.mode === 'all' || allPageSelected) {
        return { mode: 'ids', ids: current.mode === 'ids' ? current.ids.filter((id) => !pageIds.includes(id)) : [] }
      }
      return { mode: 'ids', ids: [...new Set([...current.ids, ...pageIds])] }
    })
  }

  return {
    selection,
    selectedCount,
    allPageSelected,
    somePageSelected,
    isSelected: (id: string) => selection.mode === 'all' || selection.ids.includes(id),
    toggleSelectAllPage,
    toggleSelect: (id: string) => {
      setSelection((current) => downgradeAllSelection(current, pageIds, id))
    },
    selectAllResults: () => {
      setState({ filterKey, selection: { mode: 'all', filter, totalCount } })
    },
    clearSelection: () => {
      setState({ filterKey, selection: emptyTableSelection() })
    },
    deselect: (id: string) => {
      setSelection((current) => deselectTableRow(current, pageIds, id))
    },
  }
}
